// Hook + Provider de permissoes granulares (Onda A3).
//
// O <PermsProvider> faz UM fetch em /comercial/propostas-geradas com action
// `op_meu_perfil_perms` no mount, e expoe pode(codigo) pra qualquer componente
// filho. Cache backend: 5 min em memoria do Lambda. Cache frontend: vida do
// componente Provider (recarrega se voltar com `recarregar()`).
//
// Uso:
//   const { pode, carregando } = usePerms();
//   if (pode('operacional.contrato.aba.financeiro.ver')) { ... }
//
// Pra esconder elementos UI, prefira o componente <Se> em components/Se.tsx.

import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface PermsCtx {
  carregando: boolean;
  /** true quando NÃO foi possível carregar a matriz (rede, 5XX, timeout).
   *  Diferente de "carregou e o usuário não tem nada": quem lê precisa
   *  distinguir os dois, senão esconde o sistema inteiro de quem tem acesso. */
  erro: boolean;
  codigos: Set<string>;
  pode: (codigo: string) => boolean;
  recarregar: () => Promise<void>;
}

const Ctx = createContext<PermsCtx>({
  carregando: true,
  erro: false,
  codigos: new Set(),
  pode: () => false,
  recarregar: async () => {},
});

const TENTATIVAS = 3;
const ESPERA_MS = [400, 1200];

export function PermsProvider({ children }: { children: ReactNode }) {
  const [codigos, setCodigos] = useState<Set<string>>(new Set());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  async function carregar() {
    setCarregando(true);
    let ultimoErro: any = null;

    // Incidente 2026-09-01: a mesma Lambda que responde isto roda a sincronização
    // do PNCP; das 17h10 às 17h25 ela devolveu erro, e quem abriu o ERP no
    // intervalo ficou com o menu vazio (a falha era lida como "não pode nada").
    // Agora: tenta de novo, e uma falha final NÃO apaga o que o usuário pode.
    for (let i = 0; i < TENTATIVAS; i++) {
      try {
        const token = await getToken();
        const r = await axios.post(
          `${API_URL}/comercial/propostas-geradas`,
          { action: 'op_meu_perfil_perms' },
          { headers: { Authorization: token }, timeout: 20000 }
        );
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (!Array.isArray(d.codigos)) {
          // 200 com corpo de erro conta como falha, não como "lista vazia".
          throw new Error('resposta sem lista de permissões');
        }
        setCodigos(new Set(d.codigos));
        setErro(false);
        setCarregando(false);
        return;
      } catch (e: any) {
        ultimoErro = e;
        if (i < TENTATIVAS - 1) {
          await new Promise((ok) => setTimeout(ok, ESPERA_MS[i] ?? 1200));
        }
      }
    }

    console.warn('[usePerms] falha carregando permissoes:', ultimoErro?.message);
    setErro(true);      // quem monta o menu cai no critério antigo (grupos)
    setCarregando(false); // e NÃO zeramos `codigos`: sem prova, não se tira acesso
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  return (
    <Ctx.Provider value={{
      carregando,
      erro,
      codigos,
      pode: (c) => codigos.has(c),
      recarregar: carregar,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePerms() {
  return useContext(Ctx);
}
